import { useState, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import atoms from '@Components/Utilities/SandhillsAtoms';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import TextField from '@Components/Shared/Controls/TextField';

const parseLocationValue = (location = '') => {
    const trimmedLocation = (location || '').trim();
    if (!trimmedLocation) {
        return null;
    }

    const parts = trimmedLocation.split(',').map(part => part.trim()).filter(part => part);
    if (parts.length <= 0) {
        return null;
    }

    return {
        City: parts[0] || '',
        State: parts.length > 1 ? parts[1] : '',
        Country: parts.length > 2 ? parts.slice(2).join(', ') : ''
    };
};

const normalizeLocation = (location) => {
    if (!location) {
        return null;
    }

    if (typeof location === 'string') {
        return parseLocationValue(location);
    }

    return {
        City: location.City || '',
        State: location.State || '',
        Country: location.Country || ''
    };
};

const formatLocation = (location) => {
    if (!location) {
        return '';
    }

    const normalized = typeof location === 'string' ? parseLocationValue(location) : normalizeLocation(location);
    if (!normalized || !normalized.City) {
        return '';
    }

    return [normalized.City, normalized.State, normalized.Country].filter(segment => segment).join(', ');
};

const normalizeAlertData = (data = {}) => {
    const cityLocations = Array.isArray(data.CityLocations) && data.CityLocations.length > 0
        ? data.CityLocations.map(normalizeLocation).filter(Boolean)
        : (Array.isArray(data.Cities) ? data.Cities.map(normalizeLocation).filter(Boolean) : []);

    const routes = Array.isArray(data.Routes)
        ? data.Routes.map(route => {
            const leavingFromLocation = normalizeLocation(route?.LeavingFromLocation || route?.LeavingFrom);
            const goingToLocation = normalizeLocation(route?.GoingToLocation || route?.GoingTo);

            return {
                LeavingFrom: route?.LeavingFrom || formatLocation(leavingFromLocation),
                GoingTo: route?.GoingTo || formatLocation(goingToLocation),
                LeavingFromLocation: leavingFromLocation,
                GoingToLocation: goingToLocation
            };
        })
        : [];

    return {
        LeavingFrom: data.LeavingFrom || '',
        GoingTo: data.GoingTo || '',
        CityName: data.CityName || '',
        Routes: routes,
        Cities: cityLocations.map(formatLocation),
        CityLocations: cityLocations,
        LeavingFromLocationInput: normalizeLocation(data.LeavingFromLocationInput),
        GoingToLocationInput: normalizeLocation(data.GoingToLocationInput),
        CityNameLocationInput: normalizeLocation(data.CityNameLocationInput)
    };
};

const EmptyLegAlertInputForm = (props) => {
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);

    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [responseError, setResponseError] = useState('');

    const [alertData, setAlertData] = useState(normalizeAlertData(props));
    const [originalAlertData, setOriginalAlertData] = useState(normalizeAlertData(props));

    const textChange = (e) => {
        const { name, value } = e.target;

        if (name === 'LeavingFrom') {
            setAlertData((previousState) => ({ ...previousState, [name]: value, LeavingFromLocationInput: null }));
            return;
        }

        if (name === 'GoingTo') {
            setAlertData((previousState) => ({ ...previousState, [name]: value, GoingToLocationInput: null }));
            return;
        }

        if (name === 'CityName') {
            setAlertData((previousState) => ({ ...previousState, [name]: value, CityNameLocationInput: null }));
            return;
        }

        setAlertData((previousState) => ({ ...previousState, [name]: value }));
    };

    const getAutoCompleteUrl = (e, url) => {
        return url;
    };

    const getAutoCompleteData = (e) => {
        return {
            text: e.target.value
        };
    };

    const getLocationFromSuggestion = (item = {}) => {
        return normalizeLocation({
            City: item.city,
            State: item.state,
            Country: item.country
        }) || parseLocationValue(item.value);
    };

    const getLocationAutocompleteCallback = (fieldName, locationFieldName) => {
        return (event, ui) => {
            const selectedLocation = getLocationFromSuggestion(ui?.item || {});
            setResponseError('');
            setAlertData((previousState) => ({
                ...previousState,
                [fieldName]: ui?.item?.value || '',
                [locationFieldName]: selectedLocation
            }));
        };
    };

    const addRoute = (e) => {
        e.preventDefault();
        const leavingFrom = alertData.LeavingFrom.trim();
        const goingTo = alertData.GoingTo.trim();
        const leavingFromLocation = alertData.LeavingFromLocationInput;
        const goingToLocation = alertData.GoingToLocationInput;

        if ((leavingFrom && !leavingFromLocation?.City) || (goingTo && !goingToLocation?.City)) {
            setResponseError('Please select both route locations from the city/airport list.');
            return;
        }

        if (!leavingFromLocation?.City || !goingToLocation?.City) {
            return;
        }

        setResponseError('');
        setAlertData((previousState) => ({
            ...previousState,
            Routes: [
                ...previousState.Routes,
                {
                    LeavingFrom: formatLocation(leavingFromLocation),
                    GoingTo: formatLocation(goingToLocation),
                    LeavingFromLocation: leavingFromLocation,
                    GoingToLocation: goingToLocation
                }
            ],
            LeavingFrom: '',
            GoingTo: '',
            LeavingFromLocationInput: null,
            GoingToLocationInput: null
        }));
    };

    const removeRoute = (indexToRemove) => {
        setAlertData((previousState) => ({
            ...previousState,
            Routes: previousState.Routes.filter((route, index) => index !== indexToRemove)
        }));
    };

    const addCity = (e) => {
        e.preventDefault();
        const cityName = alertData.CityName.trim();
        const cityLocation = alertData.CityNameLocationInput;

        if (cityName && !cityLocation?.City) {
            setResponseError('Please select the city from the city/airport autocomplete list.');
            return;
        }

        if (!cityLocation?.City) {
            return;
        }

        setResponseError('');
        setAlertData((previousState) => ({
            ...previousState,
            Cities: [...previousState.Cities, formatLocation(cityLocation)],
            CityLocations: [...(previousState.CityLocations || []), cityLocation],
            CityName: '',
            CityNameLocationInput: null
        }));
    };

    const removeCity = (indexToRemove) => {
        setAlertData((previousState) => ({
            ...previousState,
            Cities: previousState.Cities.filter((city, index) => index !== indexToRemove),
            CityLocations: (previousState.CityLocations || []).filter((city, index) => index !== indexToRemove)
        }));
    };

    const submitForm = (e) => {
        e.preventDefault();

        if ((alertData.LeavingFrom.trim() && !alertData.LeavingFromLocationInput?.City)
            || (alertData.GoingTo.trim() && !alertData.GoingToLocationInput?.City)
            || (alertData.CityName.trim() && !alertData.CityNameLocationInput?.City)) {
            setResponseError('Please select location values from the city/airport autocomplete list before saving.');
            return;
        }

        const errors = run(alertData, [
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);

        if (Object.keys(errors).length <= 0) {
            setResponseError('');
            setSuccess(false);
            setDisableSubmit(true);
            onSubmit();
        }
    };

    const onSubmit = () => {
        sandhillsRequest({
            url: routes.EmptyLegAlertSave || '/ajax/EmptyLegs/SaveAlert',
            method: 'post',
            data: alertData,
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            if (response.success || response.EmptyLegAlertID) {
                const normalizedResponse = normalizeAlertData({ ...alertData, ...response });
                setSuccess(true);
                setAlertData(normalizedResponse);
                setOriginalAlertData(normalizedResponse);

                // Call the parent's onSave callback
                if (props.onSave) {
                    props.onSave(normalizedResponse);
                }
            } else {
                setResponseError(response.message || 'An error occurred while saving the alert.');
            }
        }).catch(() => {
            setResponseError('An error occurred while saving the alert.');
        }).finally(() => {
            setDisableSubmit(false);
        });
    };

    const hasChanges = () => {
        return JSON.stringify(alertData) !== JSON.stringify(originalAlertData);
    };

    return (
        <form className='empty-leg-alert-input-form' onSubmit={submitForm}>
            <div className='form'>
                <div className='alert-selector'>
                    <h3>Routes</h3>
                    <div className='route-btns'>
                        <div className='btn-container'>
                            {alertData.Routes.map((route, index) => (
                                <button type='button' key={`route-${index}`} onClick={() => removeRoute(index)}>
                                    <span>{formatLocation(route.LeavingFromLocation) || route.LeavingFrom}</span>
                                    <i className='fa-solid fa-arrow-right-long'></i>
                                    <span>{formatLocation(route.GoingToLocation) || route.GoingTo}</span>
                                    <i className='fa-regular fa-circle-xmark'></i>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className='flex-row'>
                        <div className='form-row'>
                            <TextField
                                id='LeavingFrom'
                                name='LeavingFrom'
                                type='text'
                                placeholder='Leaving From'
                                class='input-container'
                                value={alertData.LeavingFrom}
                                onFieldChanged={textChange}
                                updateField={(value) => setAlertData((previousState) => ({ ...previousState, LeavingFrom: value }))}
                                autoComplete={{
                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                    postData: (e) => getAutoCompleteData(e),
                                    callback: getLocationAutocompleteCallback('LeavingFrom', 'LeavingFromLocationInput')
                                }}
                            />
                        </div>
                        <div className='form-row'>
                            <TextField
                                id='GoingTo'
                                name='GoingTo'
                                type='text'
                                placeholder='Going To'
                                class='input-container'
                                value={alertData.GoingTo}
                                onFieldChanged={textChange}
                                updateField={(value) => setAlertData((previousState) => ({ ...previousState, GoingTo: value }))}
                                autoComplete={{
                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                    postData: (e) => getAutoCompleteData(e),
                                    callback: getLocationAutocompleteCallback('GoingTo', 'GoingToLocationInput')
                                }}
                            />
                        </div>
                        <button type='button' className='add-btn' onClick={addRoute}>
                            <i className='fa-solid fa-plus'></i><span>Add</span>
                        </button>
                    </div>
                </div>

                <div className='alert-selector'>
                    <h3>Cities</h3>
                    <div className='route-btns'>
                        <div className='btn-container'>
                            {(alertData.CityLocations || []).map((city, index) => (
                                <button type='button' key={`city-${index}`} onClick={() => removeCity(index)}>
                                    <span>{formatLocation(city) || alertData.Cities[index]}</span>
                                    <i className='fa-regular fa-circle-xmark'></i>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className='flex-row'>
                        <div className='form-row'>
                            <TextField
                                id='CityName'
                                name='CityName'
                                type='text'
                                placeholder='Enter city name here'
                                class='input-container'
                                value={alertData.CityName}
                                onFieldChanged={textChange}
                                updateField={(value) => setAlertData((previousState) => ({ ...previousState, CityName: value }))}
                                autoComplete={{
                                    ajaxUrl: (e) => getAutoCompleteUrl(e, routes.GetCityAirportCodeAutoComplete),
                                    postData: (e) => getAutoCompleteData(e),
                                    callback: getLocationAutocompleteCallback('CityName', 'CityNameLocationInput')
                                }}
                            />
                        </div>
                        <button type='button' className='add-btn' onClick={addCity}>
                            <i className='fa-solid fa-plus'></i><span>Add</span>
                        </button>
                    </div>
                </div>
            </div>

            <ValidationErrors errors={validationErrors} />
            {responseError && <div className='error'>{responseError}</div>}
            {success && <div className='success'>Saved.</div>}

            <div className='modal-buttons'>
                <button type='submit' className='save-modal' disabled={disableSubmit || !hasChanges()}>
                    Save
                </button>
                <button
                    type='button'
                    className='close-modal'
                    onClick={() => {
                        if (props.onClose) {
                            props.onClose();
                        }
                    }}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default EmptyLegAlertInputForm;
